import React from "react";
import styled from "styled-components";
import {
  BacktestingRunType,
  BotData,
  BotType,
  LanguageNameSpaces,
} from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { isSymbol } from "@shared/types/utils";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import BacktestStatusTag from "features/bots/BotForm/Backtesting/common/BacktestStatusTag";
import { BacktestIcon } from "features/bots/BotForm/Backtesting/NewLook/BacktestCells";
import {
  formatTimeframe,
  getTimeframeDays,
} from "features/bots/BotForm/Backtesting/NewLook/format";

interface BacktestDrawerTitleProps {
  run?: BacktestingRunType;
  botData?: BotData;
}

/**
 * New look: the results drawer's header, left-aligned like the bot drawer's —
 * what was tested (icon and name) with its status, and underneath what it
 * traded, where and over which period.
 */
const BacktestDrawerTitle = ({ run, botData }: BacktestDrawerTitleProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const botType =
    botData?.bot_type === BotType.SIGNAL ? BotType.SIGNAL : BotType.ASAP;
  const symbol =
    run?.symbol ??
    (botData?.symbols && !isSymbol(botData.symbols)
      ? botData.symbols[0]
      : undefined);
  const subject =
    botType === BotType.SIGNAL
      ? run?.sub_group_name ?? botData?.sub_group_name
      : symbol;
  const days = getTimeframeDays(run?.backtesting_time_range);
  const sub = [
    subject,
    run?.client_name ?? botData?.client_name,
    run?.backtesting_time_range &&
      `${formatTimeframe(run.backtesting_time_range)}${
        days ? ` · ${days} ${t("backtesting.labels.days")}` : ""
      }`,
  ].filter(Boolean);
  const name = run?.name ?? botData?.name;

  return (
    <Wrapper>
      <BacktestIcon
        run={{ ...run, symbol } as BacktestingRunType}
        botType={botType}
        size={40}
      />
      <Texts>
        <NameRow>
          <Name title={name}>{name}</Name>
          <BacktestStatusTag backtestingRun={run} />
        </NameRow>
        {sub.length > 0 && <Sub>{sub.join(" · ")}</Sub>}
      </Texts>
    </Wrapper>
  );
};

export default BacktestDrawerTitle;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-width: 0;
  padding-right: 36px;
  text-align: left;
`;
const Texts = styled.div`
  flex: 1;
  min-width: 0;
`;
// On a narrow drawer the status wraps under a long name.
const NameRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0 12px;
  min-width: 0;
`;
const Name = styled.div`
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  font-size: 18px;
  line-height: 26px;
  font-weight: 500;
`;
const Sub = styled.div`
  margin-top: 1px;
  ${newLookType.meta};
  font-size: 12.5px;
  line-height: 18px;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
